export type Cell = number | null;
export type Series = Cell[];
export type SkipSeries = Array<string[] | null>;

export type FormulaNode =
  | { t: 'num'; v: number }
  | { t: 'str'; v: string }
  | { t: 'null' }
  | { t: 'ref'; name: string }
  | { t: 'neg'; a: FormulaNode }
  | { t: 'bin'; op: '+' | '-' | '*' | '/'; a: FormulaNode; b: FormulaNode }
  | { t: 'fn'; name: string; args: FormulaNode[] };

export interface CalcRow {
  k: string;
  key?: string;
  name?: string;
  src?: string;
  formula?: string;
  fallback?: string;
  lever?: string | null;
  scenarioPick?: boolean;
  fill?: string | null;
  min?: number | null;
  max?: number | null;
  totalsMode?: string | null;
  totalsFormula?: string | null;
  rollup?: string | null;
  overwrite?: string | null;
}

export interface ModelFunctionContext {
  i: number;
  args: FormulaNode[];
  value: (node: FormulaNode) => Cell;
  series: (node: FormulaNode) => Series;
  text: (node: FormulaNode) => string;
}

export type ModelFunction = (ctx: ModelFunctionContext) => Cell;

export interface EvaluateOptions {
  n: number;
  cut: number;
  resolve: (token: string) => string | null;
  input: (row: CalcRow, i: number) => Cell;
  scenario: (lever: string, i: number) => number;
  functions?: Record<string, ModelFunction>;
  override?: (row: CalcRow, i: number) => Cell;
  members?: (id: string, i: number) => Cell[];
  external?: (token: string, i: number) => Cell;
}

export interface OverwriteCell {
  input: number;
  applied: boolean;
}

export interface EvaluateResult {
  values: Record<string, Series>;
  skipped: Record<string, SkipSeries>;
  cyclic: string[];
  overwrite: Record<string, Array<OverwriteCell | null>>;
}

export const INPUT_SOURCES = ['input', 'binding', 'snowflake', 'fitted'];
export const BUILTIN_FUNCTIONS = ['SUM', 'COALESCE', 'IF', 'MIN', 'MAX', 'AVERAGE', 'ABS', 'ACROSS', 'ACROSS_COHORTS', 'NULL'];
export const MODEL_FUNCTIONS = ['SCENARIO', 'SOURCES', 'SOURCE_RATIO', 'COHORT_ORDERS', 'BASE_ORDERS', 'PRODUCT_COGS_PCT'];
export const FORMULA_FUNCTIONS = BUILTIN_FUNCTIONS.concat(MODEL_FUNCTIONS);

export const isInputSource = (src?: string) => INPUT_SOURCES.indexOf(String(src || '').toLowerCase()) >= 0;
export const OVERWRITE_INPUT = 'INPUT';
export const isOverwriteInput = (token: string) => String(token || '').trim().toUpperCase() === OVERWRITE_INPUT;
export const overwriteRefs = (text?: string | null) => formulaRefs(parseFormula(text)).filter((t) => !isOverwriteInput(t));
const overwriteOf = (row: CalcRow) => (String(row.src || '').toLowerCase() === 'input' ? parseFormula(row.overwrite) : null);
export const scenarioLever = (row: CalcRow) =>
  row.lever || (row.scenarioPick || String(row.src || '').toLowerCase() === 'input' ? row.k : null);
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

const parseCache = new Map<string, FormulaNode | null>();

export function parseFormula(text?: string | null): FormulaNode | null {
  const src = String(text || '').trim();
  if (!src) return null;
  if (parseCache.has(src)) return parseCache.get(src) || null;
  let node: FormulaNode | null = null;
  try {
    node = new Parser(src).parse();
  } catch {
    node = null;
  }
  if (parseCache.size > 2000) parseCache.clear();
  parseCache.set(src, node);
  return node;
}

class Parser {
  private tokens: string[];
  private pos = 0;

  constructor(src: string) {
    const re = /\s*("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|[A-Za-z_][A-Za-z0-9_.]*|\d+(?:\.\d+)?|[+\-*/(),])/y;
    const out: string[] = [];
    let at = 0;
    while (at < src.length) {
      re.lastIndex = at;
      const m = re.exec(src);
      if (!m) {
        if (/^\s+$/.test(src.slice(at))) break;
        throw new Error('token');
      }
      out.push(m[1]);
      at = re.lastIndex;
    }
    this.tokens = out;
  }

  parse(): FormulaNode {
    const node = this.expr();
    if (this.pos !== this.tokens.length) throw new Error('trailing');
    return node;
  }

  private peek() {
    return this.tokens[this.pos];
  }

  private take() {
    const t = this.tokens[this.pos++];
    if (t === undefined) throw new Error('eof');
    return t;
  }

  private expr(): FormulaNode {
    let node = this.term();
    while (this.peek() === '+' || this.peek() === '-') {
      const op = this.take() as '+' | '-';
      node = { t: 'bin', op, a: node, b: this.term() };
    }
    return node;
  }

  private term(): FormulaNode {
    let node = this.unary();
    while (this.peek() === '*' || this.peek() === '/') {
      const op = this.take() as '*' | '/';
      node = { t: 'bin', op, a: node, b: this.unary() };
    }
    return node;
  }

  private unary(): FormulaNode {
    if (this.peek() === '-') {
      this.take();
      return { t: 'neg', a: this.unary() };
    }
    return this.primary();
  }

  private primary(): FormulaNode {
    const t = this.take();
    if (t === '(') {
      const node = this.expr();
      if (this.take() !== ')') throw new Error('paren');
      return node;
    }
    if (/^\d/.test(t)) return { t: 'num', v: +t };
    if (t[0] === '"' || t[0] === "'") return { t: 'str', v: t.slice(1, -1) };
    if (!/^[A-Za-z_]/.test(t)) throw new Error('unexpected');
    if (this.peek() === '(') {
      this.take();
      const args: FormulaNode[] = [];
      if (this.peek() !== ')') {
        args.push(this.expr());
        while (this.peek() === ',') {
          this.take();
          args.push(this.expr());
        }
      }
      if (this.take() !== ')') throw new Error('call');
      return { t: 'fn', name: t.toUpperCase(), args };
    }
    if (t.toUpperCase() === 'NULL') return { t: 'null' };
    return { t: 'ref', name: t };
  }
}

export function formulaRefs(node: FormulaNode | null, out: string[] = []): string[] {
  if (!node) return out;
  if (node.t === 'ref') {
    if (out.indexOf(node.name) < 0) out.push(node.name);
  } else if (node.t === 'neg') formulaRefs(node.a, out);
  else if (node.t === 'bin') {
    formulaRefs(node.a, out);
    formulaRefs(node.b, out);
  } else if (node.t === 'fn') node.args.forEach((a) => formulaRefs(a, out));
  return out;
}

export function formulaFunctions(node: FormulaNode | null, out: string[] = []): string[] {
  if (!node) return out;
  if (node.t === 'fn') {
    if (out.indexOf(node.name) < 0) out.push(node.name);
    node.args.forEach((a) => formulaFunctions(a, out));
  } else if (node.t === 'neg') formulaFunctions(node.a, out);
  else if (node.t === 'bin') {
    formulaFunctions(node.a, out);
    formulaFunctions(node.b, out);
  }
  return out;
}

const LEVER_ARG: Record<string, number> = { SCENARIO: 0, BASE_ORDERS: 2 };

export function formulaLevers(text: string | null | undefined): string[] {
  const out: string[] = [];
  const walk = (node: FormulaNode | null) => {
    if (!node) return;
    if (node.t === 'neg') walk(node.a);
    else if (node.t === 'bin') {
      walk(node.a);
      walk(node.b);
    } else if (node.t === 'fn') {
      const at = LEVER_ARG[node.name];
      const arg = at === undefined ? null : node.args[at];
      if (arg && arg.t === 'str' && out.indexOf(arg.v) < 0) out.push(arg.v);
      node.args.forEach(walk);
    }
  };
  walk(parseFormula(text));
  return out;
}

export function isValidFormula(text: string | null | undefined, resolve: (token: string) => string | null): boolean {
  const node = parseFormula(text);
  if (!node) return false;
  if (formulaFunctions(node).some((f) => FORMULA_FUNCTIONS.indexOf(f) < 0)) return false;
  return formulaRefs(node).every((t) => !!resolve(t));
}

export const ROLLUP_SOURCE = 'rollup';
export const ROLLUP_FUNCTIONS = ['SUM', 'AVG', 'WAVG', 'MIN', 'MAX'];
export const isRollupSource = (src?: string) => String(src || '').toLowerCase() === ROLLUP_SOURCE;

const isAggregate = (node: FormulaNode): boolean =>
  node.t === 'fn' &&
  ROLLUP_FUNCTIONS.indexOf(node.name) >= 0 &&
  node.args.length === (node.name === 'WAVG' ? 2 : 1) &&
  node.args.every((a) => a.t === 'ref');

export function isValidRollup(text: string | null | undefined, resolve?: (token: string) => string | null): boolean {
  let aggregates = 0;
  const walk = (node: FormulaNode): boolean => {
    if (node.t === 'num') return true;
    if (node.t === 'ref') return !resolve || !!resolve(node.name);
    if (node.t === 'neg') return walk(node.a);
    if (node.t === 'bin') return walk(node.a) && walk(node.b);
    if (node.t === 'fn' && isAggregate(node)) {
      aggregates++;
      return node.args.every(walk);
    }
    return false;
  };
  const node = parseFormula(text);
  return !!node && walk(node) && aggregates > 0;
}

function rollupRefs(node: FormulaNode | null, out: string[] = []): string[] {
  if (!node) return out;
  if (node.t === 'ref') {
    if (out.indexOf(node.name) < 0) out.push(node.name);
  } else if (node.t === 'neg') rollupRefs(node.a, out);
  else if (node.t === 'bin') {
    rollupRefs(node.a, out);
    rollupRefs(node.b, out);
  } else if (node.t === 'fn' && !isAggregate(node)) node.args.forEach((a) => rollupRefs(a, out));
  return out;
}

export function aggregate(name: string, vals: Cell[], weights?: Cell[] | null): Cell {
  if (name === 'WAVG') {
    let num = 0;
    let den = 0;
    let any = false;
    vals.forEach((v, m) => {
      const w = weights ? weights[m] : null;
      if (!isNum(v) || !isNum(w)) return;
      num += v * w;
      den += w;
      any = true;
    });
    return any && den !== 0 ? num / den : null;
  }
  const present = vals.filter(isNum);
  if (!present.length) return null;
  if (name === 'SUM') return present.reduce((a, b) => a + b, 0);
  if (name === 'AVG') return present.reduce((a, b) => a + b, 0) / present.length;
  if (name === 'MIN') return Math.min(...present);
  if (name === 'MAX') return Math.max(...present);
  return null;
}

const calcFormula = (row: CalcRow) =>
  isRollupSource(row.src) ? null : isInputSource(row.src) ? row.fallback : row.formula;

export function evaluateRows(rows: CalcRow[], opts: EvaluateOptions): EvaluateResult {
  const { n, cut } = opts;
  const byId: Record<string, CalcRow> = {};
  rows.forEach((r) => {
    if (r && r.k && !byId[r.k]) byId[r.k] = r;
  });
  const ids = Object.keys(byId);
  const values: Record<string, Series> = {};
  const skipped: Record<string, SkipSeries> = {};
  ids.forEach((id) => {
    values[id] = new Array(n).fill(null);
    skipped[id] = new Array(n).fill(null);
  });

  const parsed: Record<string, FormulaNode | null> = {};
  const rollups: Record<string, FormulaNode | null> = {};
  const overwrites: Record<string, FormulaNode | null> = {};
  const deps: Record<string, string[]> = {};
  ids.forEach((id) => {
    const row = byId[id];
    const node = parseFormula(calcFormula(row));
    const rule = opts.members && (isRollupSource(row.src) || isInputSource(row.src)) ? parseFormula(row.rollup) : null;
    const overwrite = overwriteOf(row);
    parsed[id] = node;
    rollups[id] = rule;
    overwrites[id] = overwrite;
    deps[id] = formulaRefs(node)
      .concat(rollupRefs(rule))
      .concat(formulaRefs(overwrite).filter((t) => !isOverwriteInput(t)))
      .map((t) => opts.resolve(t))
      .filter((d, k, all): d is string => !!d && !!byId[d] && d !== id && all.indexOf(d) === k);
  });

  const sortRows = () => {
    const sorted: string[] = [];
    const state: Record<string, number> = {};
    const loops: string[] = [];
    const visit = (id: string, stack: string[]) => {
      if (state[id] === 2) return;
      if (state[id] === 1) {
        stack.slice(stack.indexOf(id)).forEach((c) => {
          if (loops.indexOf(c) < 0) loops.push(c);
        });
        return;
      }
      state[id] = 1;
      stack.push(id);
      deps[id].forEach((d) => visit(d, stack));
      stack.pop();
      state[id] = 2;
      sorted.push(id);
    };
    ids.forEach((id) => visit(id, []));
    return { sorted, loops };
  };
  let { sorted: order, loops: cyclic } = sortRows();
  const softLoops = cyclic.filter((id) => isInputSource(byId[id].src) && deps[id].length);
  if (softLoops.length) {
    softLoops.forEach((id) => {
      deps[id] = [];
    });
    ({ sorted: order, loops: cyclic } = sortRows());
  }

  const seriesCache = new WeakMap<FormulaNode, Series>();
  let selfInput: Cell | undefined;

  const evalAt = (node: FormulaNode, i: number, own: string[], inherited: string[], rollup = false): Cell => {
    switch (node.t) {
      case 'num':
        return node.v;
      case 'null':
      case 'str':
        return null;
      case 'ref': {
        if (selfInput !== undefined && isOverwriteInput(node.name)) return selfInput;
        const id = opts.resolve(node.name);
        if (!id || !values[id]) {
          const x = opts.external ? opts.external(node.name, i) : null;
          return isNum(x) ? x : null;
        }
        const v = values[id][i];
        const sk = skipped[id] ? skipped[id][i] : null;
        if (isNum(v) && sk) sk.forEach((l) => inherited.push(l));
        return isNum(v) ? v : null;
      }
      case 'neg': {
        const v = evalAt(node.a, i, own, inherited, rollup);
        return v === null ? null : -v;
      }
      case 'bin': {
        const a = evalAt(node.a, i, own, inherited, rollup);
        const b = evalAt(node.b, i, own, inherited, rollup);
        if (a === null || b === null) return null;
        if (node.op === '+') return a + b;
        if (node.op === '-') return a - b;
        if (node.op === '*') return a * b;
        return b === 0 ? null : a / b;
      }
      case 'fn':
        return rollup && isAggregate(node) ? aggregateAt(node, i) : callAt(node, i, own, inherited);
    }
    return null;
  };

  const aggregateAt = (node: Extract<FormulaNode, { t: 'fn' }>, i: number): Cell => {
    const membersOf = (arg?: FormulaNode) => {
      const id = arg && arg.t === 'ref' ? opts.resolve(arg.name) : null;
      return id && opts.members ? opts.members(id, i) : null;
    };
    const vals = membersOf(node.args[0]);
    return vals ? aggregate(node.name, vals, node.args.length > 1 ? membersOf(node.args[1]) : null) : null;
  };

  const clamp = (row: CalcRow, v: Cell): Cell => {
    if (!isNum(v)) return null;
    if (isNum(row.min)) v = Math.max(row.min, v);
    if (isNum(row.max)) v = Math.min(row.max, v);
    return v;
  };

  const rootCauses = (node: FormulaNode, i: number, seen: Set<string>, out: string[]) => {
    formulaRefs(node).forEach((t) => {
      const id = opts.resolve(t);
      if (!id || !byId[id] || seen.has(id)) return;
      seen.add(id);
      if (isNum(values[id][i])) return;
      const row = byId[id];
      const inner = parsed[id];
      const before = out.length;
      if (!isInputSource(row.src) && inner && seen.size < 24) rootCauses(inner, i, seen, out);
      if (out.length === before) out.push(row.name || row.k);
    });
  };

  const labelsFor = (node: FormulaNode, i: number) => {
    if (node.t === 'ref') {
      const id = opts.resolve(node.name);
      const row = id ? byId[id] : null;
      return row ? [row.name || row.k] : [];
    }
    const out: string[] = [];
    rootCauses(node, i, new Set<string>(), out);
    return out;
  };

  const seriesOf = (node: FormulaNode): Series => {
    const hit = seriesCache.get(node);
    if (hit) return hit;
    const out: Series = new Array(n).fill(null);
    for (let i = 0; i < n; i++) out[i] = evalAt(node, i, [], []);
    seriesCache.set(node, out);
    return out;
  };

  const callAt = (node: Extract<FormulaNode, { t: 'fn' }>, i: number, own: string[], inherited: string[]): Cell => {
    const args = node.args;
    const arg = (k: number) => (args[k] ? evalAt(args[k], i, own, inherited) : null);
    switch (node.name) {
      case 'SUM': {
        let acc: Cell = null;
        const missing: string[] = [];
        args.forEach((a) => {
          const v = evalAt(a, i, own, inherited);
          if (v === null) labelsFor(a, i).forEach((l) => missing.push(l));
          else acc = (acc === null ? 0 : acc) + v;
        });
        if (acc !== null) missing.forEach((l) => own.push(l));
        return acc;
      }
      case 'COALESCE': {
        for (let k = 0; k < args.length; k++) {
          const v = evalAt(args[k], i, own, inherited);
          if (v !== null) return v;
          if (k < args.length - 1) labelsFor(args[k], i).forEach((l) => own.push(l));
        }
        return null;
      }
      case 'IF': {
        const c = arg(0);
        return c ? arg(1) : arg(2);
      }
      case 'MIN':
      case 'MAX': {
        const vals = args.map((a) => evalAt(a, i, own, inherited));
        if (vals.some((v) => v === null)) return null;
        return node.name === 'MIN' ? Math.min(...(vals as number[])) : Math.max(...(vals as number[]));
      }
      case 'AVERAGE': {
        let sum = 0;
        let count = 0;
        args.forEach((a) => {
          const v = evalAt(a, i, own, inherited);
          if (v !== null) {
            sum += v;
            count++;
          }
        });
        return count ? sum / count : null;
      }
      case 'ABS': {
        const v = arg(0);
        return v === null ? null : Math.abs(v);
      }
      case 'ACROSS':
        return arg(args.length - 1);
      case 'ACROSS_COHORTS':
        return arg(0);
      case 'SCENARIO': {
        const lever = args[0] && args[0].t === 'str' ? args[0].v : '';
        return opts.scenario(lever, i);
      }
      default: {
        const fn = opts.functions && opts.functions[node.name];
        if (!fn) return null;
        const v = fn({
          i,
          args,
          value: (a) => evalAt(a, i, own, inherited),
          series: seriesOf,
          text: (a) => (a.t === 'str' ? a.v : a.t === 'ref' ? a.name : ''),
        });
        return isNum(v) ? v : null;
      }
    }
  };

  const overwriteValue = (node: FormulaNode, raw: number, i: number, own: string[], inherited: string[]): Cell => {
    selfInput = raw;
    try {
      return evalAt(node, i, own, inherited);
    } finally {
      selfInput = undefined;
    }
  };

  const overwriteCells: Record<string, Array<OverwriteCell | null>> = {};

  const inputValue = (row: CalcRow, i: number, node: FormulaNode | null, own: string[], inherited: string[], rolled: Series | null, overwrite: FormulaNode | null): Cell => {
    let v = opts.input(row, i);
    const fc = i > cut;
    if (!fc && overwrite && isNum(v)) {
      const ownOw: string[] = [];
      const inheritedOw: string[] = [];
      const result = overwriteValue(overwrite, v, i, ownOw, inheritedOw);
      const applied = isNum(result);
      (overwriteCells[row.k] = overwriteCells[row.k] || new Array(n).fill(null))[i] = { input: v, applied };
      if (applied) {
        ownOw.forEach((l) => own.push(l));
        inheritedOw.forEach((l) => inherited.push(l));
        return clamp(row, result);
      }
    }
    if (!isNum(v) && fc && row.fill === 'carry') {
      for (let t = Math.min(i, cut); t >= 0; t--) {
        const prev = rolled ? rolled[t] : opts.input(row, t);
        if (isNum(prev)) {
          v = prev;
          break;
        }
      }
    }
    if (isNum(v)) {
      const lever = scenarioLever(row);
      if (fc && lever) v *= opts.scenario(lever, i);
    } else v = node ? evalAt(node, i, own, inherited) : null;
    return clamp(row, v);
  };

  order.forEach((id) => {
    const row = byId[id];
    if (cyclic.indexOf(id) >= 0) return;
    const node = parsed[id];
    const rule = rollups[id];
    const rolled = isRollupSource(row.src);
    for (let i = 0; i < n; i++) {
      const own: string[] = [];
      const inherited: string[] = [];
      const forced = opts.override ? opts.override(row, i) : null;
      let v: Cell;
      if (isNum(forced)) v = forced;
      else if (rule && (rolled || i <= cut)) v = clamp(row, evalAt(rule, i, own, inherited, true));
      else if (rolled) v = null;
      else if (isInputSource(row.src)) v = inputValue(row, i, node, own, inherited, rule || overwrites[id] ? values[id] : null, overwrites[id]);
      else {
        v = node ? evalAt(node, i, own, inherited) : null;
        if (row.scenarioPick && i > cut && isNum(v)) v *= opts.scenario(scenarioLever(row) as string, i);
      }
      v = isNum(v) ? v : null;
      values[id][i] = v;
      if (v !== null && !isNum(forced)) {
        const list: string[] = [];
        inherited.concat(own).forEach((l) => {
          if (list.indexOf(l) < 0) list.push(l);
        });
        skipped[id][i] = list.length ? list : null;
      }
    }
  });

  return { values, skipped, cyclic, overwrite: overwriteCells };
}

export type ExcelCall = (name: string, args: FormulaNode[], emit: (node: FormulaNode) => string | null) => string | null;

export function formulaToExcel(text: string | null | undefined, ref: (token: string) => string | null, call?: ExcelCall): string | null {
  const root = parseFormula(text);
  if (!root) return null;
  const numeric = (node: FormulaNode, s: string) =>
    node.t === 'num' || node.t === 'neg' || (node.t === 'bin' && node.op !== '/') || (node.t === 'fn' && ['SUM', 'MIN', 'MAX'].indexOf(node.name) >= 0) ? s : 'N(' + s + ')';
  const emit = (node: FormulaNode, top = false): string | null => {
    switch (node.t) {
      case 'num':
        return String(node.v);
      case 'null':
      case 'str':
        return null;
      case 'ref':
        return ref(node.name);
      case 'neg': {
        const a = emit(node.a);
        return a === null ? null : '-' + numeric(node.a, a);
      }
      case 'bin': {
        const a0 = emit(node.a);
        const b0 = emit(node.b);
        if (a0 === null || b0 === null) return null;
        const a = numeric(node.a, a0), b = numeric(node.b, b0);
        if (node.op === '/') return 'IFERROR(' + a + '/' + b + ',"")';
        if (node.op === '*' && b === '1') return a;
        if (node.op === '*' && a === '1') return b;
        const body = a + node.op + b;
        return top ? body : '(' + body + ')';
      }
      case 'fn': {
        const args = node.args.map((a) => emit(a, true));
        const present = args.filter((a): a is string => a !== null);
        switch (node.name) {
          case 'SUM':
          case 'MIN':
          case 'MAX':
          case 'AVERAGE':
            return present.length ? node.name + '(' + present.join(',') + ')' : null;
          case 'ABS':
            return args[0] ? 'ABS(' + args[0] + ')' : null;
          case 'COALESCE':
            return present.length ? present.slice(0, -1).reduceRight((acc, a) => 'IF(ISNUMBER(' + a + '),' + a + ',' + acc + ')', present[present.length - 1]) : null;
          case 'IF':
            return args[0] ? 'IF(' + args[0] + ',' + (args[1] ?? '""') + ',' + (args[2] ?? '""') + ')' : null;
          default:
            return call ? call(node.name, node.args, (a) => emit(a, true)) : null;
        }
      }
    }
    return null;
  };
  return emit(root, true);
}

export function cohortEntrantsToken(text: string | null | undefined, block: string): string | null {
  let found: string | null = null;
  const walk = (node: FormulaNode | null): void => {
    if (!node || found) return;
    if (node.t === 'neg') return walk(node.a);
    if (node.t === 'bin') { walk(node.a); walk(node.b); return; }
    if (node.t !== 'fn') return;
    const [b, e] = node.args;
    if (node.name === 'COHORT_ORDERS' && b && b.t === 'str' && b.v === block && e && e.t === 'ref') { found = e.name; return; }
    node.args.forEach(walk);
  };
  walk(parseFormula(text));
  return found;
}

export const TOTALS_MODES = ['add_up', 'formula', 'no_totals'];

export const totalsModeOf = (row?: { totalsMode?: string | null } | null): string => {
  const mode = String((row && row.totalsMode) || '').toLowerCase();
  return mode === 'formula' || mode === 'no_totals' ? mode : 'add_up';
};

export function sumCells(vals: Cell[]): Cell {
  let acc: Cell = null;
  vals.forEach((v) => {
    if (isNum(v)) acc = (acc === null ? 0 : acc) + v;
  });
  return acc;
}

export function isArithmeticFormula(text: string | null | undefined): boolean {
  const walk = (node: FormulaNode): boolean => {
    if (node.t === 'num' || node.t === 'ref') return true;
    if (node.t === 'neg') return walk(node.a);
    if (node.t === 'bin') return walk(node.a) && walk(node.b);
    return false;
  };
  const node = parseFormula(text);
  return !!node && walk(node);
}

export function evaluateArithmetic(node: FormulaNode | null, ref: (token: string) => Cell): Cell {
  if (!node) return null;
  switch (node.t) {
    case 'num':
      return node.v;
    case 'ref': {
      const v = ref(node.name);
      return isNum(v) ? v : null;
    }
    case 'neg': {
      const v = evaluateArithmetic(node.a, ref);
      return v === null ? null : -v;
    }
    case 'bin': {
      const a = evaluateArithmetic(node.a, ref);
      const b = evaluateArithmetic(node.b, ref);
      if (a === null || b === null) return null;
      if (node.op === '+') return a + b;
      if (node.op === '-') return a - b;
      if (node.op === '*') return a * b;
      return b === 0 ? null : a / b;
    }
  }
  return null;
}

export function combineTotals(rows: CalcRow[], resolve: (token: string) => string | null, sumOf: (id: string) => Cell): Record<string, Cell> {
  const byId: Record<string, CalcRow> = {};
  rows.forEach((r) => {
    if (r && r.k && !byId[r.k]) byId[r.k] = r;
  });
  const out: Record<string, Cell> = {};
  const busy = new Set<string>();
  const totalOf = (id: string): Cell => {
    if (id in out) return out[id];
    if (busy.has(id)) return null;
    busy.add(id);
    const row = byId[id];
    const mode = totalsModeOf(row);
    let v: Cell = null;
    if (mode === 'add_up') v = sumOf(id);
    else if (mode === 'formula') {
      v = evaluateArithmetic(parseFormula(row.totalsFormula), (token) => {
        const ref = resolve(token);
        if (!ref || !byId[ref]) return null;
        return totalsModeOf(byId[ref]) === 'no_totals' ? sumOf(ref) : totalOf(ref);
      });
    }
    busy.delete(id);
    out[id] = v;
    return v;
  };
  Object.keys(byId).forEach(totalOf);
  return out;
}

export interface RollupPart {
  label: string;
  values: Record<string, Series>;
  skipped: Record<string, SkipSeries>;
}

export interface RollupResult {
  values: Record<string, Series>;
  raw: Record<string, Series>;
  skipped: Record<string, SkipSeries>;
}

export function rollupChannels(rows: CalcRow[], parts: RollupPart[], n: number, resolve: (token: string) => string | null): RollupResult {
  const values: Record<string, Series> = {};
  const raw: Record<string, Series> = {};
  const skipped: Record<string, SkipSeries> = {};
  rows.forEach((r) => {
    raw[r.k] = new Array(n).fill(null);
    values[r.k] = new Array(n).fill(null);
    skipped[r.k] = new Array(n).fill(null);
    for (let t = 0; t < n; t++) {
      raw[r.k][t] = sumCells(parts.map((p) => (p.values[r.k] ? p.values[r.k][t] : null)));
      const list: string[] = [];
      parts.forEach((p) => {
        const own = p.skipped[r.k] ? p.skipped[r.k][t] : null;
        if (own && own.length) list.push(p.label + ': ' + own.join(', '));
      });
      skipped[r.k][t] = list.length ? list : null;
    }
  });
  for (let t = 0; t < n; t++) {
    const totals = combineTotals(rows, resolve, (id) => (raw[id] ? raw[id][t] : null));
    rows.forEach((r) => {
      values[r.k][t] = totals[r.k];
      if (totals[r.k] === null) skipped[r.k][t] = null;
    });
  }
  return { values, raw, skipped };
}

export function skippedNote(skipped: Record<string, SkipSeries> | null | undefined, key: string, idxs: number[]): string {
  const series = skipped && skipped[key];
  if (!series) return '';
  const list: string[] = [];
  idxs.forEach((t) => (series[t] || []).forEach((l) => {
    if (list.indexOf(l) < 0) list.push(l);
  }));
  return list.length ? 'Empty · skipped in this total: ' + list.join(' · ') : '';
}
